import { ISharePointParameters } from "../interfaces/ISharePointParameters";
import { ITag } from "@fluentui/react";
import { CopyMoveErrors, FileSortOptionKeys, piiConfig, sharepoint, sharepointType } from "../config/constants";
import { IFile } from "../interfaces/IFile";
import { IPager } from "../interfaces/IPager";
import { IFilePreCommitRequest } from "../interfaces/IFilePreCommitRequest";
import { IFilePreCommitResult } from "../interfaces/IFilePreCommitResult";
import { logger } from "../../../utilities/logger";
import { axiosRetryGet, axiosRetryPost } from "../../../utilities/AxiosRetryUtil";
import { IViewInfo } from "@pnp/sp/views";
import { IBasePermissions, IListInfo, ISearchResponse, PermissionKind, sp } from "@pnp/sp/presets/all";
import { FilterSection, currentSharepointFolderUserPermissions } from "../redux/models/reduxModels";
import { ISortOption } from "../interfaces/ISortOption";
import { encodePath, tryEncodeUriComponent } from "../../../utilities/EncodeDecode";
import { ComponentTypes } from "../config/config";
import { IPageSettings } from "../interfaces/IPageSettings";
import { IOneDriveRootDetails } from "../interfaces/IOneDriveRootDetails";
import { getListItemWebUrl } from "./GraphService";
import { AxiosResponse } from "axios";
import { IOfficeContextApp } from "../components/helpers/officeHelper";
import { IHubSite } from "../interfaces/IHubSite";
import { ICreateCopyJobsRequest } from "../interfaces/ICreateCopyJobsRequest";
import { FolderPreCommitRequest } from "../components/_v2/redux/api/local/api-local-interfaces";
import { apiFilePreCommitCheck, apiFolderPreCommitCheck } from "./ApiService";
import { parseString } from "xml2js";
import { CacheHelper } from "../../../utilities/CacheHelper";
import { ILibraryDefault } from "../interfaces/ILibraryDefaults";
import { GetIANATimeZoneName } from "../../../utilities/TimeZone/SharePointTimeZones";
import authModule from "../../../utilities/AuthModule";
import { NewDocumentTemplate } from "../interfaces/IDocumentTemplate";
import { getExtension, removeExtension } from "../components/helpers/stringHelper";
import { ISiteUser } from "../interfaces/ISiteUser";
import { buildLinearRecursiveCAML, escapeXml } from "../../../contentManager/Panels/CMFileFilterPanel/FileFilterHelper";
import { ISiteLocaleInfo } from "../interfaces/ISiteLocaleInfo";
import * as lcid from "../../../utilities/lcid/lcid";
import { ISearchResultCell } from "../interfaces/ISitesSearchResponse";
import { ISiteInfo } from "../interfaces/ISiteInfo";

const tryDecodeUriComponent = (uriComponent: string): string => {
  try {
    return decodeURIComponent(uriComponent)
  } catch (error) {
    // Ignore
  }
  return uriComponent;
}

// const encodeServerRelativePath = (serverRelativeUrl: string): string => {
//   let serverRelativeUrlParts = serverRelativeUrl.split("/");
//   for (let index = 0; index < serverRelativeUrlParts.length; index++) {
//     let urlPart = serverRelativeUrlParts[index];
//     urlPart = tryDecodeUriComponent(urlPart);
//     serverRelativeUrlParts[index] = tryEncodeUriComponent(urlPart);
//   }

//   return serverRelativeUrlParts.join("%2F").replaceAll("'", "''");
// }

const getSharePointSiteContent = async (
  accessToken: string,
  sharepointParameter: ISharePointParameters
): Promise<any[]> => {
  try {
    let response = await axiosRetryGet(`${sharepointParameter.webUrl}/_api/web/lists(guid'${sharepointParameter.sharePointListId}')/${sharepointParameter.parameter}?${sharepointParameter.odataQuery}`,
      {
        headers: { Authorization: `Bearer ${accessToken}`, accept: "application/json; odata=nometadata" }
      });

    return response?.data?.value;
  } catch (err) {
    logger(err, "SharePointRestService.ts (getSharePointSiteContent)");
    return [];
  }
};

export const getSharepointFiles = async (
  pager: IPager,
  accessToken: string,
  webUrl: string,
  driveId: string,
  sortOption: ISortOption,
  currentDirectory: IPageSettings,
  oneDriveRootDetails: IOneDriveRootDetails,
  context: IOfficeContextApp,
  filters?: FilterSection[]
): Promise<{ files: IFile[], nextPageUrl: string }> => {
  try {
    const isInOneDrive = (currentDirectory.type == ComponentTypes.OneDrive || currentDirectory.type == ComponentTypes.OneDriveSubFolder);

    let folderServerRelativeUrl: string;
    try {
      if (currentDirectory.serverRelativePath) {
        folderServerRelativeUrl = tryDecodeUriComponent(currentDirectory?.serverRelativePath);
      }
      else if (currentDirectory?.directory?.length > 0 && currentDirectory?.parentId) {
        let driveId = isInOneDrive ? oneDriveRootDetails.driveId : (currentDirectory.parentId ? currentDirectory.parentId : currentDirectory.id);
        const webUrl = await getListItemWebUrl(await authModule.getGraphAccessToken(), driveId, currentDirectory?.id);
        folderServerRelativeUrl = tryDecodeUriComponent(new URL(webUrl).pathname);
      }
    } catch (error) {
      logger(error, "SharePointRestService.ts (Try set folderServerRelativeUrl)");
    }

    let viewQuery: string;
    const baseUrl = `${webUrl}/_api/web/lists('${isInOneDrive ? oneDriveRootDetails.sharePointListId : currentDirectory.sharePointListId}')/RenderListDataAsStream`;
    let url: string;
    if (pager?.nextPageUrl) {
      url = `${baseUrl}${pager.nextPageUrl}`;
    } else {
      url = baseUrl + "?FilterField1=FSObjType&FilterValue1=0";

      if (Array.isArray(filters) && filters.length > 0) {
        let filterFieldCount = 2;
        const combinedQueryClauses: string[] = [];

        filters.forEach(section => {
          const sectionQueryConditions: string[] = [];

          if (section.daysOffset || section.daysOffset === 0) {
            let condition: string;
            if (section.daysOffset < 0) {
              // If the offset is negative, return dates from the past up to today
              condition = `<And><Geq><FieldRef Name="${section.filterField.RealFieldName}"/><Value Type="${section.filterField.Type}"><Today OffsetDays="${section.daysOffset}"/></Value></Geq><Leq><FieldRef Name="${section.filterField.RealFieldName}"/><Value Type="${section.filterField.Type}"><Today/></Value></Leq></And>`;
            } else if (section.daysOffset === 0) {
              // If the offset is zero, return results for today
              condition = `<Eq><FieldRef Name="${section.filterField.RealFieldName}"/><Value Type="${section.filterField.Type}"><Today/></Value></Eq>`;
            } else if (section.daysOffset === 1) {
              // If the offset is 1, return results only for tomorrow
              condition = `<Eq><FieldRef Name="${section.filterField.RealFieldName}"/><Value Type="${section.filterField.Type}"><Today OffsetDays="${section.daysOffset}"/></Value></Eq>`;
            } else if (section.daysOffset > 1 && section.daysOffset <= 92) {
              // If the offset is between 1 and 92 (inclusive), return results from tomorrow up to the specified offset
              condition = `<And><Geq><FieldRef Name="${section.filterField.RealFieldName}"/><Value Type="${section.filterField.Type}"><Today /></Value></Geq><Leq><FieldRef Name="${section.filterField.RealFieldName}"/><Value Type="${section.filterField.Type}"><Today OffsetDays="${section.daysOffset}"/></Value></Leq></And>`;
            } else {
              // If the offset is 93 or higher, return results from tomorrow onwards
              condition = `<Geq><FieldRef Name="${section.filterField.RealFieldName}"/><Value Type="${section.filterField.Type}"><Today /></Value></Geq>`;
            }
            sectionQueryConditions.push(condition);
          }
          else if (section.datasourceMutated) {
            section.items
              .filter(x => x.selected)
              .forEach(filterItem => {
                sectionQueryConditions.push(`<Contains><FieldRef Name="${escapeXml(section.filterField.RealFieldName)}"/><Value Type="${escapeXml(section.filterField.Type)}">${escapeXml(filterItem.value)}</Value></Contains>`);
              });
          }
          else if (section.items.some(x => x.selected)) {
            let selectedCount = section.items.filter(x => x.selected).length;
            let isWordSelected = section.items.some(x => x.selected && x.fileType === 'docx');
            let isExcelSelected = section.items.some(x => x.selected && x.fileType === 'xlsx');
            let isPowerPointSelected = section.items.some(x => x.selected && x.fileType === 'pptx');
            let isVisioSelected = section.items.some(x => x.selected && x.fileType === 'vsdx');

            let fieldValues = section.items.filter(x => x.selected).map(item => item.value).join('%3B%23');
            if (selectedCount > 1) {
              fieldValues = fieldValues.replaceAll(";", "%3B%3B");
            }

            const filterField = `FilterField${selectedCount > 1 ? 's' : (isWordSelected || isExcelSelected || isPowerPointSelected || isVisioSelected) ? 's' : ''}${filterFieldCount}=${section.filterField.RealFieldName}`;
            const filterValue = `FilterValue${selectedCount > 1 ? 's' : (isWordSelected || isExcelSelected || isPowerPointSelected || isVisioSelected) ? 's' : ''}${filterFieldCount}=${fieldValues}`;
            const filterType = `FilterType${selectedCount > 1 ? 's' : (isWordSelected || isExcelSelected || isPowerPointSelected || isVisioSelected) ? 's' : ''}${filterFieldCount}=${section.filterField.Type}`;
            const filterOp = `FilterOp${filterFieldCount}=In`;

            url += `&${filterField}&${filterValue.replaceAll("&", "%26")}&${filterType}&${filterOp}`;

            filterFieldCount++;
          }

          const sectionQueryClause = buildLinearRecursiveCAML(sectionQueryConditions, "Or");
          if (sectionQueryClause) {
            combinedQueryClauses.push(sectionQueryClause);
          }
        });

        if (combinedQueryClauses.length > 0) {
          const whereClause = buildLinearRecursiveCAML(combinedQueryClauses, "And");
          if (whereClause) {
            viewQuery = `<Query><Where>${whereClause}</Where></Query>`;
          }
        }
      }
      else if (context) {
        url += `&FilterFields2=DocIcon&FilterValues2=${context.filter.values}&FilterTypes2=Computed&FilterOp2=In`;
      }

      url += `&SortField=${sortOption?.sortField || FileSortOptionKeys.SortFields.TimeLastModified}&SortDir=${sortOption?.sortOrder || FileSortOptionKeys.SortOrder.Descending}`;
    }

    let data = {
      parameters: {
        DatesInUtc: true,
        ViewXml: !viewQuery
          ? sharepoint.defaultViewXml
          : sharepoint.getDefaultFilteredViewXml(viewQuery)
      }
    }

    if (folderServerRelativeUrl) {
      data.parameters["FolderServerRelativeUrl"] = folderServerRelativeUrl;
    }

    let retry: boolean;
    const requestConfig = {
      headers: { Authorization: `Bearer ${accessToken}`, accept: "application/json; odata=nometadata" }
    };
    let response: AxiosResponse<any>;
    do {
      try {
        response = await axiosRetryPost(url, data, requestConfig, false);
        /**
        * If a sort option was specified without a next page href, it indicates that there should be data.
        * Retry with default sort options to ensure data is returned.
        */
        if (response?.data?.Row?.length === 0 && !pager?.nextPageUrl && sortOption?.sortField) {
          url = `${baseUrl}?FilterField1=FSObjType&FilterValue1=0&SortField=${FileSortOptionKeys.SortFields.TimeLastModified}&SortDir=${FileSortOptionKeys.SortOrder.Descending}`;
          retry = true;
        }
        else {
          break;
        }
      } catch (error) {
        // Retry throttling.
        if (error?.response?.data["odata.error"]?.code?.startsWith("-2147024860")) {
          data.parameters["ReplaceGroup"] = true;
          retry = true;
        }
        else {
          retry = false;
        }
      }
    } while (retry);

    const sharepointFiles: IFile[] = response?.data?.Row?.map(item => {
      let file: IFile = {
        id: item.UniqueId.replace('{', '').replace('}', ''),
        name: item.FileLeafRef,
        driveId: driveId,
        createdDateTime: item.Created,
        lastModifiedDateTime: item.Modified,
        modifiedBy: item.Editor[0]?.title,
        fileType: item.File_x0020_Type,
        size: item.File_x0020_Size,
        permMask: item.PermMask,
        renderItemID: item.ID,
        serverRelativePath: item.FileRef?.replace(`/${item.FileLeafRef}`, "")
      }

      const privacySensitive = item[piiConfig.privacySentitiveColumnName];
      if (privacySensitive !== undefined) {
        file.pii = {
          piiDetected: privacySensitive === "Yes" || privacySensitive === "True",
          pii: item[piiConfig.privacySentitiveDataColumnName]
        }
      }

      return file;
    });

    return {
      files: sharepointFiles ?? [],
      nextPageUrl: response?.data?.NextHref
    };

  } catch (err) {
    logger(err, "SharePointRestService.ts (getSharePointFiles)");
    return {
      files: [],
      nextPageUrl: ''
    };
  }
};

export const getSharepointListSchema = async (accessToken: string, siteUrl: string, sharePointListId: string) => {
  try {
    const data = {
      parameters: {
        RenderOptions: 4
      }
    }
    const requestConfig = {
      headers: { Authorization: `Bearer ${accessToken}`, accept: "application/json; odata=nometadata" }
    };
    const response: AxiosResponse<any> = await axiosRetryPost(`${siteUrl}/_api/web/lists('${sharePointListId}')/RenderListDataAsStream`, data, requestConfig, false);
    return response?.data;
  } catch (err) {
    logger(err, "SharePointRestService.ts (getSharepointListSchema)");
    return null;
  }
};

export const getSharepointLibraryFiles = async (siteUrl: string, listTitle: string): Promise<NewDocumentTemplate[]> => {
  try {
    const requestConfig = {
      headers: { Authorization: `Bearer ${await authModule.getSharePointAccessToken()}`, Accept: "application/json;odata=verbose" }
    };
    const response: AxiosResponse<any> = await axiosRetryGet(`${siteUrl}/_api/web/lists/GetByTitle('${listTitle}')/items?$expand=file&$select=file/UniqueId,file/Name,file/ServerRelativeUrl`, requestConfig);
    const files: any[] = response?.data?.d?.results?.map(f => f.File);
    if (Array.isArray(files) && files.length > 0) {
      return files.map(f => {
        return {
          templateId: f.UniqueId,
          title: removeExtension(f.Name),
          extention: getExtension(f.Name),
          url: f.ServerRelativeUrl
        }
      });
    }
  } catch (err) {
    logger(err, "SharePointRestService.ts (getSharepointLibraryFiles)");
  }
  return [];
};

export const getFilePreCommitInfo = async (request: IFilePreCommitRequest): Promise<IFilePreCommitResult> => {
  try {
    return await apiFilePreCommitCheck(request);
  } catch {
    return {
      exists: false,
      checkedOut: false,
      checkedOutBy: "",
      locked: false,
      lockedBy: ""
    };
  }
};

export const getFolderPreCommitInfo = async (request: FolderPreCommitRequest): Promise<IFilePreCommitResult> => {
  try {
    return await apiFolderPreCommitCheck(request);
  } catch {
    return {
      exists: false,
      checkedOut: false,
      checkedOutBy: "",
      locked: false,
      lockedBy: ""
    };
  }
};

export const getSharePointGroupUsers = async (
  accessToken: string,
  webUrl: string,
  userSelectionScope: string
): Promise<any[]> => {
  try {
    let response = await axiosRetryGet(`${webUrl}/_api/Web/SiteGroups/GetById(${userSelectionScope})/users?$select=Id,Title`,
      {
        headers: { Authorization: `Bearer ${accessToken}` }
      });

    return response?.data?.value;
  } catch (err) {
    logger(err, "SharePointRestService.ts (getSharePointGroupUsers)");
    return [];
  }
};

const getSharePointSiteUsersCall = async (accessToken: string, webUrl: string): Promise<any[]> => {
  try {
    let response = await axiosRetryGet(`${webUrl}/_api/web/siteusers?$select=Id,Title,Email,UserPrincipalName&$filter=(Email ne '' or (UserPrincipalName ne '' and UserPrincipalName ne null))`, {
      headers: { Authorization: `Bearer ${accessToken}` }
    });

    return response?.data?.value;
  } catch (err) {
    logger(err, "SharePointRestService.ts (getSharePointSiteUsersCall)");
    return [];
  }
};

export const getSharePointContentTypes = async (
  accessToken: string,
  webUrl: string,
  documentLibrary: string,
  includeParentData: boolean = false
): Promise<any[]> =>
  getSharePointSiteContent(accessToken, {
    webUrl: webUrl,
    sharePointListId: documentLibrary,
    parameter: "contentTypes",
    odataQuery: !includeParentData ? "$select=id,name,sealed" : "$select=id,name,sealed,parent/id&$expand=parent/id"
  });

export const getSharePointContentTypesWithFields = async (
  accessToken: string,
  webUrl: string,
  documentLibrary: string
): Promise<any[]> =>
  getSharePointSiteContent(accessToken, {
    webUrl: webUrl,
    sharePointListId: documentLibrary,
    parameter: "contentTypes",
    odataQuery: "$select=id,name,sealed,fields/Group,fields/Hidden,fields/InternalName,fields/Required&$expand=fields"
  });

export const getSharePointContentTypeFields = async (
  accessToken: string,
  webUrl: string,
  documentLibrary: string,
  contentTypeId: string,
  query: string
): Promise<any[]> =>
  getSharePointSiteContent(accessToken, {
    webUrl: webUrl,
    sharePointListId: documentLibrary,
    parameter: contentTypeId ? `contentTypes('${contentTypeId}')/fields` : "fields",
    odataQuery: query
  });

export const getSharePointDocumentLibraryFields = async (
  accessToken: string,
  webUrl: string,
  documentLibrary: string,
  query: string
): Promise<any[]> =>
  getSharePointSiteContent(accessToken, {
    webUrl: webUrl,
    sharePointListId: documentLibrary,
    parameter: `/fields`,
    odataQuery: query
  });

export const getSharePointSiteUsers = async (accessToken: string, webUrl: string): Promise<any[]> => {
  return await getSharePointSiteUsersCall(accessToken, webUrl);
};

export const getSharePointSiteUsersById = async (webUrl: string, idList: number[]): Promise<ISiteUser[]> => {
  if (Array.isArray(idList)) {
    try {
      const filter: string = idList.map(id => `(Id eq ${id})`).join(" or ");
      const response = (await axiosRetryGet(`${webUrl}/_api/web/siteuserinfolist/items?$select=Id,Title,EMail,UserName&$filter=${filter}`,
        {
          headers: { Authorization: `Bearer ${await authModule.getSharePointAccessToken()}`, accept: "application/json; odata=nometadata" }
        }));

      if (Array.isArray(response?.data?.value)) {
        return response.data.value;
      }
    } catch (err) {
      logger(err, "SharePointRestService.ts (getSharePointSiteUsersById)");
    }
  }
  return [];
}

export const searchSharePointSiteUsers = async (webUrl: string, filterValue: string): Promise<ISiteUser[]> => {
  try {
    filterValue = filterValue.replaceAll("'", "''");
    const response = (await axiosRetryGet(`${webUrl}/_api/web/siteuserinfolist/items?$select=Id,Title,EMail,UserName&$filter=(substringof('${filterValue}',Title) or substringof('${filterValue}',UserName) or substringof('${filterValue}',EMail)) and (EMail ne null or UserName ne null)`,
      {
        headers: { Authorization: `Bearer ${await authModule.getSharePointAccessToken()}`, accept: "application/json; odata=nometadata" }
      }));

    if (Array.isArray(response?.data?.value)) {
      return response.data.value;
    }
  } catch (err) {
    logger(err, "SharePointRestService.ts (searchSharePointSiteUsers)");
  }
  return [];
}

export const searchSharePointGroup = async (webUrl: string, groupToSearch: string, allowGroups: boolean, filterValue: string): Promise<ISiteUser[]> => {
  /*PrincipalSource - The principal sources to search:

    All (15) - Search all principal sources.
    MembershipProvider (4) - Search the current membership provider.
    None (0) - Search no principal sources.
    RoleProvider (8) - Search the current role provider.
    UserInfoList (1) - Search the user information list.
    Windows (2) - Search active directory.

    PrincipalType - The principal types to return:

    All (15) - Return all entity types.
    DistributionList (2) - Return distribution list entity types.
    None (0) - Return no principal types.
    SecurityGroup (4) - Return security group entity types.
    SharePointGroup (8) - Return sharepoint group entity types.
    User (1) - Return user entity types.
*/
  try {
    const response = (await axiosRetryPost(`${webUrl}/_api/SP.UI.ApplicationPages.ClientPeoplePickerWebServiceInterface.ClientPeoplePickerSearchUser`,
      {
        queryParams: {
          QueryString: filterValue,
          MaximumEntitySuggestions: 50,
          AllowEmailAddresses: false,
          AllowOnlyEmailAddresses: false,
          PrincipalType: allowGroups ? 5 : 1,
          PrincipalSource: 15,
          SharePointGroupID: Number.parseInt(groupToSearch)
        }
      },
      {
        headers: { Authorization: `Bearer ${await authModule.getSharePointAccessToken()}`, accept: "application/json;odata=verbose" }
      }));

    if (response?.data?.d?.ClientPeoplePickerSearchUser) {
      const jsonResult = JSON.parse(response.data.d.ClientPeoplePickerSearchUser);
      if (Array.isArray(jsonResult)) {
        return jsonResult.map(x => ({
          Id: -1,
          Title: x.DisplayText ?? x.EntityData?.DisplayName,
          EMail: x.EntityData?.Email
        }));
      }
    }
  } catch (err) {
    logger(err, "SharePointRestService.ts (searchSharePointSiteUsers)");
  }
  return [];
}

export const getTaxonomyTerms = async (accessToken: string, termSetId: string, webUrl: string): Promise<ITag[]> => {
  try {
    const data = `<Request xmlns="http://schemas.microsoft.com/sharepoint/clientquery/2009" SchemaVersion="15.0.0.0" LibraryVersion="16.0.0.0" ApplicationName="PnPjs"><Actions><ObjectPath Id="1" ObjectPathId="0" /><ObjectPath Id="3" ObjectPathId="2" /><Query Id="4" ObjectPathId="2"><Query SelectAllProperties="true" ><Properties /></Query ></Query ><ObjectPath Id="6" ObjectPathId="5" /><ObjectPath Id="8" ObjectPathId="7" /><Query Id="9" ObjectPathId="7"><Query SelectAllProperties="true" ><Properties /></Query ><ChildItemQuery SelectAllProperties="false" ><Properties><Property Name="Id" SelectAll="true" /><Property Name="Name" SelectAll="true" /><Property Name="IsDeprecated" SelectAll="true" /><Property Name="IsAvailableForTagging" SelectAll="true" /><Property Name="PathOfTerm" SelectAll="true" /><Property Name="TermsCount" SelectAll="true" /><Property Name="Parent" SelectAll="true" /><Property Name="Labels" SelectAll="true" /><Property Name="TermSet" SelectAll="true" /></Properties></ChildItemQuery ></Query ></Actions><ObjectPaths><StaticMethod Id="0" Name="GetTaxonomySession" TypeId="{981cbc68-9edc-4f8d-872f-71146fcbb84f}" /><Method Id="2" ParentId="0" Name="GetDefaultSiteCollectionTermStore"></Method><Method Id="5" ParentId="2" Name="GetTermSet"><Parameters><Parameter Type="String">${termSetId}</Parameter></Parameters></Method><Method Id="7" ParentId="5" Name="GetAllTerms"></Method></ObjectPaths></Request>`;
    let response = await axiosRetryPost(`${webUrl}/_vti_bin/client.svc/ProcessQuery?`, data, {
      headers: { Authorization: `Bearer ${accessToken}` }
    });

    return response.data
      .filter(item => item._ObjectType_ === sharepointType.taxonomyTerm)
      .reduce((agg, cur) => [...agg, ...cur._Child_Items_], [])
      .map(item => {
        return {
          key: item.Id.replace("/Guid(", "").replace(")/", ""),
          name: item.Name
        };
      });
  } catch (err) {
    logger(err, "SharePointRestService.ts (getTaxonomyTerms)");
    return [];
  }
};

export const getSharePointDocumentSetSettingsXml = async (
  accessToken: string,
  webUrl: string,
  sharePointListId: string,
  contentTypeId: string,
): Promise<any> => {
  try {
    let response = await axiosRetryGet(`${webUrl}/_api/web/lists(guid'${sharePointListId}')/contentTypes('${contentTypeId}')?$select=SchemaXml`,
      {
        headers: { Authorization: `Bearer ${accessToken}`, accept: "application/json; odata=nometadata" }
      });

    return response?.data;
  } catch (err) {
    logger(err, "SharePointRestService.ts (getSharePointDocumentSetSettingsXml)");
    return undefined;
  }
};

export const getSharePointListViews = async (accessToken: string, webUrl: string, sharePointListId: string): Promise<IViewInfo[]> => {
  try {
    let response = await axiosRetryGet(`${webUrl}/_api/web/lists(guid'${sharePointListId}')/views?$select=Id,Title,DefaultView,ListViewXml,ViewType2&$filter=Hidden eq false`,
      {
        headers: { Authorization: `Bearer ${accessToken}`, accept: "application/json; odata=nometadata" }
      });

    return response?.data?.value;
  } catch (err) {
    logger(err, "SharePointRestService.ts (getSharePointListViews)");
    return undefined;
  }
};

export const getUserEffectivePermissions = async (accessToken: string, webUrl: string, sharePointListId?: string, sharePointUniqueId?: string, username?: string): Promise<currentSharepointFolderUserPermissions> => {
  try {
    const encodedUsername: string = username && tryEncodeUriComponent(`i:0#.f|membership|${username.replaceAll("'", "''")}`);
    const baseUrl: string = !sharePointListId
      ? `${webUrl}/_api/web`
      : (!sharePointUniqueId
        ? `${webUrl}/_api/web/lists('${sharePointListId}')`
        : `${webUrl}/_api/web/lists('${sharePointListId}')/GetItemByUniqueId('${sharePointUniqueId}')`);

    let url: string = !encodedUsername
      ? `${baseUrl}/EffectiveBasePermissions`
      : `${baseUrl}/getUserEffectivePermissions('${encodedUsername}')`;

    let retryLimit: number = 3;

    const requestConfig = {
      headers: { Authorization: `Bearer ${accessToken}`, accept: "application/json; odata=nometadata" }
    };
    let perms: IBasePermissions;

    while (retryLimit > 0) {
      try {
        perms = (await axiosRetryGet(url, requestConfig)).data;
        break;
      } catch (error) {
        // Retry intermittent getUserEffectivePermissions() endpoint.
        if (!encodedUsername || error?.response?.status !== 403) {
          throw error;
        }
      }

      if (--retryLimit === 0 && encodedUsername) {
        // Last retry also failed, retry one last time with default endpoint
        perms = (await axiosRetryGet(`${baseUrl}/EffectiveBasePermissions`, requestConfig)).data;
      }
    }

    if (perms) {
      return getPermissionsFromBase(perms);
    }
  } catch (err) {
    logger(err, "SharePointRestService.ts (getUserPermissions)");
  }
  return null;
};

export const getUserEffectivePermissionsFromMask = (permHex: string): currentSharepointFolderUserPermissions => {
  if (permHex) {
    try {
      const basePerm: IBasePermissions = {
        Low: getPermMaskL(permHex),
        High: getPermMaskH(permHex)
      };
      return getPermissionsFromBase(basePerm);
    } catch (error) {
      logger(error, "SharePointRestService.ts (getUserEffectivePermissionsFromMask)");
    }
  }
  return null;
}

export const getLists = async (accessToken: string, webUrl: string): Promise<IListInfo[]> => {
  try {
    const lists = (await axiosRetryGet(`${webUrl}/_api/web/lists?$select=Id,ItemCount&$filter=Hidden eq false`,
      {
        headers: { Authorization: `Bearer ${accessToken}`, accept: "application/json; odata=nometadata" }
      })).data;

    return Array.isArray(lists?.value) && lists.value.length > 0
      ? lists.value.map(list => ({ Id: list.Id, ItemCount: list.ItemCount }))
      : [];
  } catch (err) {
    logger(err, "SharePointRestService.ts (getLists)");
    return null;
  }
};

export const getListSettings = async (accessToken: string, webUrl: string, sharePointListId: string): Promise<IListInfo> => {
  try {
    return (await axiosRetryGet(`${webUrl}/_api/web/lists('${sharePointListId}')?$select=EnableFolderCreation,ContentTypesEnabled,EnableMinorVersions,ExcludeFromOfflineClient,ForceCheckout,ItemCount`,
      {
        headers: { Authorization: `Bearer ${accessToken}`, accept: "application/json; odata=nometadata" }
      })).data;
  } catch (err) {
    logger(err, "SharePointRestService.ts (getListSettings)");
    return null;
  }
};

export const createLink = async (accessToken: string, webUrl: string, serverRelativePath: string, linkName: string, payload: any): Promise<any> => {
  try {
    const url = `${webUrl}/_api/Web/GetFolderByServerRelativePath(decodedurl='${encodePath(tryDecodeUriComponent(serverRelativePath))}')/Files/AddUsingPath(decodedUrl='${encodePath(tryDecodeUriComponent(linkName))}',overwrite=false)`
    return (await axiosRetryPost(url, payload,
      {
        headers: { Authorization: `Bearer ${accessToken}`, accept: "application/json;odata=verbose" }
      })).data?.d;
  } catch (error) {
    logger(error, "SharePointRestService.ts (createLink)");
    throw (error);
  }
}

export const getShortcutLink = async (accessToken: string, webUrl: string, fileUrl: string): Promise<any> => {
  try {
    const url = `${webUrl}/_api/SP.Utilities.ShortcutLink.GetShortcutLink(@url)?@url=${tryEncodeUriComponent(`'${fileUrl}'`)}`;
    return (await axiosRetryGet(url,
      {
        headers: { Authorization: `Bearer ${accessToken}`, accept: "application/json;odata=verbose" }
      }))?.data?.d;
  } catch (error) {
    logger(error, "SharePointRestService.ts (createLink)");
  }
  return null;
}

export const getFileByUrl = async (accessToken: string, webUrl: string, fileUrl: string): Promise<any | null> => {
  try {
    const webHost: string = new URL(webUrl).hostname.toLowerCase().trim();
    const fileHost: string = new URL(fileUrl).hostname.toLowerCase().trim();

    let requestUrl: string;
    if (webHost === fileHost) {
      requestUrl = `${webUrl}/_api/web/GetFileByUrl(@url)?@url=${tryEncodeUriComponent(`'${fileUrl}'`)}`;
    }
    else {
      requestUrl = `${webUrl}/_api/SP.RemoteWeb(@url)/web/GetFileByUrl(@url)?@url=${tryEncodeUriComponent(`'${fileUrl}'`)}`;
    }

    return (await axiosRetryGet(requestUrl,
      {
        headers: { Authorization: `Bearer ${accessToken}`, accept: "application/json;odata=verbose" }
      })).data;
  } catch (error) {
    logger(error, "SharePointRestService.ts (getFileByUrl)");
  }
  return null;
}

export const getListSharingInformation = async (accessToken: string, webUrl: string, listId: string): Promise<any> => {
  try {
    const url = `${webUrl}/_api/web/Lists('${listId}')`;
    return (await axiosRetryPost(url, {},
      {
        headers: { Authorization: `Bearer ${accessToken}`, accept: "application/json;odata=verbose" }
      }))?.data?.d;
  } catch (error) {
    logger(error, "SharePointRestService.ts (getListSharingInformation)");
  }
  return null;
}

export const getLibraryDefaultValues = async (accessToken: string, webUrl: string, directory: string): Promise<ILibraryDefault[]> => {
  if (webUrl && directory) {
    try {
      const directoryParts: string[] = directory.split("/")?.filter(v => v);
      if (Array.isArray(directoryParts) && directoryParts.length > 0) {
        const domain = new URL(webUrl).origin;
        const libraryUrl = `${webUrl.replace(domain, '')}/${directoryParts[0]}`;

        let xmlData: ILibraryDefault[] = CacheHelper.get(libraryUrl);

        if (!Array.isArray(xmlData)) {
          xmlData = [];
          try {
            const url = `${webUrl}/_api/Web/GetFileByServerRelativePath(decodedurl='${encodePath(tryDecodeUriComponent(libraryUrl))}/Forms/client_LocationBasedDefaults.html')/$value?binaryStringResponseBody=true`
            const xmlString = (await axiosRetryGet(url,
              {
                headers: { Authorization: `Bearer ${accessToken}`, accept: "application/json; odata=nometadata" }
              })).data;

            parseString(xmlString, (err, result) => {
              if (result && result.MetadataDefaults?.a) {
                xmlData = result.MetadataDefaults?.a?.map((x: any) => {
                  return {
                    folder: tryDecodeUriComponent(x.$.href),
                    values: x.DefaultValue.map((defaultValue: any) => {
                      return {
                        fieldName: defaultValue.$.FieldName,
                        value: defaultValue._
                      };
                    })
                  };
                });
              }
            });
          } catch (error) {
            // Assume no default vaues found
          }
          CacheHelper.set(libraryUrl, xmlData);
        }

        return xmlData;
      }
    } catch (err) {
      logger(err, "SharePointRestService.ts (getLibraryDefaultValues)");
    }
  }
  return null;
};

// export const getSiteTimeZoneOffset = async (accessToken: string, webUrl: string): Promise<number | null> => {
//   try {
//     const timeZone = (await axiosRetryGet(`${webUrl}/_api/web/regionalsettings/TimeZone?$select=Id,Information`,
//       {
//         headers: { Authorization: `Bearer ${accessToken}`, accept: "application/json; odata=nometadata" }
//       })).data;

//     if (timeZone) {
//       const timeZoneInfo = timeZone.Information;
//       // https://github.com/SharePoint/sp-dev-docs/issues/6083
//       // var offset = -(timeZoneInfo.Bias + timeZoneInfo.StandardBias + timeZoneInfo.DaylightBias) / 60.0 -1;
//       // return dayjs.utc(value).utcOffset(offset);
//       // return -(timeZoneInfo.Bias + timeZoneInfo.StandardBias + timeZoneInfo.DaylightBias) / 60.0 - 1;
//       return -(timeZoneInfo.Bias + timeZoneInfo.StandardBias + timeZoneInfo.DaylightBias) / 60.0 - (GetIsTimeZoneIdInDST(timeZone.Id.toString()) ? 1 : 0);
//     }
//   } catch (err) {
//     logger(err, "SharePointRestService.ts (getSiteTimeZoneOffset)");
//   }
//   return null;
// };

export const getSiteLocaleInfo = async (accessToken: string, webUrl: string): Promise<ISiteLocaleInfo | null> => {
  try {
    const localeInfo = (await axiosRetryGet(`${webUrl}/_api/web/regionalsettings?$expand=TimeZone&$select=LocaleId,TimeZone/Id`,
      {
        headers: { Authorization: `Bearer ${accessToken}`, accept: "application/json; odata=nometadata" }
      })).data;

    if (localeInfo && Number(localeInfo.LocaleId) > 0 && Number(localeInfo.TimeZone?.Id) > 0) {
      return {
        locale: lcid.from(localeInfo.LocaleId).slice(0, 2),
        timeZoneName: GetIANATimeZoneName(localeInfo.TimeZone.Id.toString())
      }
    }
  } catch (err) {
    logger(err, "SharePointRestService.ts (getSiteLocaleInfo)");
  }
  return null;
};

export const getListItemsById = async (accessToken: string, webUrl: string, sharePointListId: string, columnInternalName: string, ids: number[]): Promise<ITag[]> => {
  if (!Array.isArray(ids) || ids.length === 0) return [];
  try {
    const showField: string = columnInternalName || "Title";
    const items = (await axiosRetryGet(`${webUrl}/_api/web/lists('${sharePointListId}')/items?$select=Id,${showField}&$filter=${ids.map(id => `Id eq ${id}`).join(" or ")}`,
      {
        headers: { Authorization: `Bearer ${accessToken}`, accept: "application/json; odata=nometadata" }
      })).data;

    return Array.isArray(items?.value) && items.value.length > 0
      ? items.value.map(item => ({ key: item.Id, name: item[showField] }))
      : [];
  } catch (err) {
    logger(err, "SharePointRestService.ts (getListItemsById)");
    return null;
  }
};

export const discardCheckOut = async (accessToken: string, webUrl: string, itemUniqueId: string): Promise<void> => {
  try {
    await axiosRetryPost(`${webUrl}/_api/web/GetFileById(guid'${itemUniqueId}')/undocheckout`,
      null,
      {
        headers: { Authorization: `Bearer ${accessToken}`, accept: "application/json; odata=nometadata" }
      }, false);
  } catch (err) {
    logger(err, "SharePointRestService.ts (discardCheckOut)");
  }
};

export const getItemAbsoluteUrl = async (accessToken: string, webUrl: string, listUniqueId: string, itemUniqueId: string): Promise<string> => {
  let absUrl: string;
  try {
    absUrl = (await axiosRetryGet(`${webUrl}/_api/web/lists(guid'${listUniqueId}')/GetItemByUniqueId('${itemUniqueId}')?$select=EncodedAbsUrl`,
      {
        headers: { Authorization: `Bearer ${accessToken}`, accept: "application/json; odata=nometadata" }
      }))?.data?.EncodedAbsUrl;
  } catch (err) {
    logger(err, "SharePointRestService.ts (getItemAbsoluteUrl)");
  }
  return absUrl;
};

export const getItemVersions = async (accessToken: string, webUrl: string, listUniqueId: string, itemUniqueId: string): Promise<any> => {
  try {
    return (await axiosRetryGet(`${webUrl}/_api/web/lists(guid'${listUniqueId}')/GetItemByUniqueId('${itemUniqueId}')/Versions`,
      {
        headers: { Authorization: `Bearer ${accessToken}`, accept: "application/json; odata=nometadata" }
      }))?.data;
  } catch (err) {
    logger(err, "SharePointRestService.ts (getItemVersions)");
  }
  return null;
};

export const getListFields = async (accessToken: string, webUrl: string, listUniqueId: string): Promise<any> => {
  try {
    return (await axiosRetryGet(`${webUrl}/_api/web/lists(guid'${listUniqueId}')/fields?$filter=Hidden eq false`,
      {
        headers: { Authorization: `Bearer ${accessToken}`, accept: "application/json; odata=nometadata" }
      }))?.data;
  } catch (err) {
    logger(err, "SharePointRestService.ts (getListFields)");
  }
  return null;
};

export const getSiteLogo = async (accessToken: string, webUrl: string): Promise<string> => {
  try {
    const response: Response = await fetch(`${webUrl}/_api/siteiconmanager/getsitelogo?type='1'`, {
      headers: {
        "Authorization": `Bearer ${accessToken}`
      }
    });

    if (response.ok) {
      const url = window.URL || window.webkitURL;
      const blob = await response.blob();
      return url.createObjectURL(blob);
    }
  } catch (err) {
    // Ignore
    // logger(err, "SharePointRestService.ts (getSiteLogo)");
  }
  return undefined;
};

export const getFileVersionData = async (accessToken: string, siteUrl: string, uniqueId: string): Promise<{ Level: number, MajorVersion: number, MinorVersion: number }> => {
  try {
    const response = (await axiosRetryGet(`${siteUrl}/_api/web/GetFileById(guid'${uniqueId}')?$select=Level,MajorVersion,MinorVersion`,
      {
        headers: { Authorization: `Bearer ${accessToken}`, accept: "application/json; odata=nometadata" }
      }))?.data;

    if (response) {
      return {
        Level: response.Level,
        MajorVersion: response.MajorVersion,
        MinorVersion: response.MinorVersion
      }
    }
  } catch (err) {
    logger(err, "SharePointRestService.ts (getFileVersionData)");
  }
  return null;
};

export const getHubSites = async (accessToken: string, siteUrl: string): Promise<IHubSite[]> => {
  try {
    const response = (await axiosRetryGet(`${siteUrl}/_api/HubSites?$select=ID,SiteUrl,ParentHubSiteId,Title,Description`,
      {
        headers: { Authorization: `Bearer ${accessToken}`, accept: "application/json; odata=nometadata" }
      }))?.data;

    return Array.isArray(response?.value) && response.value.length > 0
      ? response.value.map(hubSite => ({ id: hubSite.ID, siteUrl: hubSite.SiteUrl, parentHubSiteId: hubSite.ParentHubSiteId, title: hubSite.Title, description: hubSite.Description }))
      : [];
  } catch (err) {
    logger(err, "SharePointRestService.ts (getHubSites)");
  }
  return null;
};

export const getAccessibleSites = async (siteAbsUrl: string, queryText: string): Promise<ISiteInfo[]> => {
  if (siteAbsUrl) {
    try {
      // const queryText = siteQueries.top;
      const selectProperties = "'Title,Path,WebTemplate'";
      const rowLimit = 500;

      const apiUrl = `${siteAbsUrl}/_api/search/query?querytext='${queryText}'&selectproperties=${selectProperties}&rowlimit=${rowLimit}&trimduplicates=true`;

      const response = (await axiosRetryGet(apiUrl,
        {
          headers: { Authorization: `Bearer ${await authModule.getSharePointAccessToken()}`, accept: "application/json;odata=nometadata" }
        }))?.data;

      const data: ISearchResponse = response;

      if (data && data.PrimaryQueryResult && data.PrimaryQueryResult.RelevantResults) {
        const relevantResults = data.PrimaryQueryResult.RelevantResults;
        const rows = relevantResults.Table.Rows;

        const sites: ISiteInfo[] = rows?.map((row) => {
          const cells = row.Cells as ISearchResultCell[];
          const siteName: string = getCellValue(cells, "Title");
          return {
            id: getCellValue(cells, "SiteId"),
            name: siteName,
            siteName: siteName,
            webUrl: getCellValue(cells, "Path"),
            isSite: true,
            isSubsite: false
          };
        });
        return sites ?? [];
      }
    } catch (error) {
      logger(error, "SharePointRestService.ts (getAccessibleSites)");
    }
  }
  return [];
};

export const createCopyJobs = async (args: ICreateCopyJobsRequest): Promise<{ success: boolean, jobs: any[], error?: string }> => {
  let errorMessage = "";
  try {
    const { siteUrl, files, sourceSiteUrl, sourceServerRelPath, destinationServerRelPath, isMoveMode, accessToken } = args;

    let sourcePath = sourceSiteUrl;
    if (sourceServerRelPath) {
      const domain = new URL(sourceSiteUrl).origin;
      sourcePath = `${domain}${sourceServerRelPath}`;
    }

    let destinationPath = siteUrl;
    if (destinationServerRelPath) {
      const domain = new URL(siteUrl).origin;
      destinationPath = `${domain}${destinationServerRelPath}`;
    }

    const fileNames: string[] = files.map(file => file.FileLeafRef);
    const duplicateNames: string[] = fileNames.filter((fileName, index) => fileNames.indexOf(fileName) !== index);

    if (duplicateNames.length > 0) {
      return {
        success: false,
        jobs: [],
        error: CopyMoveErrors.DuplicateFilenames
      };
    }

    const arr: { containerPath: string, filePath: string }[] = files.map(file => {
      let siteOrigin: string = new URL(sourcePath).origin;
      if (siteOrigin.endsWith("/")) {
        siteOrigin = siteOrigin.slice(0, -1);
      }

      let fileServerRelativePath: string = file.FileRef;
      if (!fileServerRelativePath.startsWith("/")) {
        fileServerRelativePath = "/" + fileServerRelativePath;
      }

      const filePath: string = new URL(siteOrigin + fileServerRelativePath).href;
      const containerPath: string = filePath.split("/").slice(0, -1).join("/");
      return {
        containerPath,
        filePath
      };
    });

    const sourceLocations = Object.values(
      groupBy((item) => item.containerPath, arr)
    );

    const requestConfig = {
      headers: { Authorization: `Bearer ${accessToken}`, accept: "application/json; odata=nometadata" }
    };

    let jobs: any[] = [];
    for (let i = 0; i < sourceLocations.length; i++) {
      const locationFiles = sourceLocations[i];
      if (Array.isArray(locationFiles) && locationFiles.length > 0) {
        const body = {
          exportObjectUris: locationFiles.map(file => {
            return file.filePath;
          }),
          destinationUri: destinationPath,
          options: {
            AllowSchemaMismatch: true,
            AllowSmallerVersionLimitOnDestination: true,
            IgnoreVersionHistory: true,
            IsMoveMode: isMoveMode,
            NameConflictBehavior: 2 //1 = REPLACE and 2 = KEEP BOTH
          }
        }

        const copyJobsResponse = (await axiosRetryPost(`${siteUrl}/_api/site/CreateCopyJobs`, body, requestConfig, false))?.data?.value;
        if (copyJobsResponse) {
          jobs = jobs.concat(copyJobsResponse);
        }
      }
    }

    if (jobs.length > 0) {
      return {
        success: true,
        jobs
      };
    }
  } catch (err) {
    logger(err, "SharePointRestService.ts (createCopyJobs)");
    errorMessage = err.response?.data["odata.error"]?.message?.value;
  }

  return {
    success: false,
    jobs: [],
    error: errorMessage
  };
};

const getPermissionsFromBase = (perms: IBasePermissions): currentSharepointFolderUserPermissions => {
  const userPermissions: currentSharepointFolderUserPermissions = {
    canAddListItem: sp.web.hasPermissions(perms, PermissionKind.AddListItems),
    canEditListItem: sp.web.hasPermissions(perms, PermissionKind.EditListItems),
    canDeleteListItem: sp.web.hasPermissions(perms, PermissionKind.DeleteListItems),
    canOpenItems: sp.web.hasPermissions(perms, PermissionKind.OpenItems),
    canViewVersions: sp.web.hasPermissions(perms, PermissionKind.ViewVersions),
    canDeleteVersions: sp.web.hasPermissions(perms, PermissionKind.DeleteVersions),
    canCancelCheckout: sp.web.hasPermissions(perms, PermissionKind.CancelCheckout),
    canApproveItems: sp.web.hasPermissions(perms, PermissionKind.ApproveItems),
    canEnumeratePermissions: sp.web.hasPermissions(perms, PermissionKind.EnumeratePermissions),
    canManagePermissions: sp.web.hasPermissions(perms, PermissionKind.ManagePermissions),
  };

  return userPermissions;
}

const getPermMaskH = (permHex: string) => {
  const a = permHex.length;
  return a <= 10 ? 0 : parseInt(permHex.substring(2, a - 8), 16);
};

const getPermMaskL = (permHex: string) => {
  const a = permHex.length;
  return a <= 10 ? parseInt(permHex) : parseInt(permHex.substring(a - 8, a), 16);
};

const groupBy = (groupingFn: (x: any) => string, arr: any[]) => {
  const groups: { [key: string]: any[] } = {}

  arr.forEach((item) => {
    const key = groupingFn(item);

    if (groups[key] !== undefined) {
      groups[key].push(item);
    } else {
      groups[key] = [item];
    }
  });

  return groups;
};

const getCellValue = (cells: ISearchResultCell[], key: string): string | undefined => {
  const cell = cells.find(c => c.Key === key);
  return cell ? cell.Value : undefined;
};